import "./MetaDocTocFilter.scss";

import DatePicker from "components/inputs/DatePicker/DatePicker";
import Toggle from "components/inputs/Toggle/Toggle";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  DATE_TO_STRING,
  getQuickDateRange,
  QUICK_DATE_RANGE_OPTIONS,
  STRING_TO_DATE,
} from "utils/date-utils";

import { getDocumentTypeLabel } from "../../../utils/documentTypeUtils";
import useAnchoredFixedMenuPosition from "./useAnchoredFixedMenuPosition";

import type { Dispatch, SetStateAction } from "react";

type TocFilterSection =
  | "documentTypes"
  | "pageRange"
  | "dateRange"
  | "summarySearch"
  | "medicationSearch"
  | "codeSearch"
  | "tocSearch";

type TocPageRangeValue = {
  startPage: number | null;
  endPage: number | null;
};

type TocDateRangeValue = {
  startDate: string | null;
  endDate: string | null;
};

type MetaDocTocFilterProps = {
  availableDocumentTypes: string[];
  selectedDocumentTypes: string[];
  onDocumentTypesChange?: Dispatch<SetStateAction<string[]>>;
  searchQuery: string;
  onSearchQueryChange?: (value: string) => void;
  pageRangeStart?: number | null;
  pageRangeEnd?: number | null;
  onPageRangeChange?: (value: TocPageRangeValue) => void;
  dateRangeStart?: string | null;
  dateRangeEnd?: string | null;
  onDateRangeChange?: (value: TocDateRangeValue) => void;
  currentPage: number;
  totalPages: number;
  hideFilteredPages?: boolean;
  onHideFilteredPagesChange?: (value: boolean) => void;
  summarySearchQuery?: string;
  onSummarySearchQueryChange?: (value: string) => void;
  onSummarySearchSubmit?: () => void;
  onSummarySearchClear?: () => void;
  isSummarySearching?: boolean;
  summarySearchResultCount?: number;
  lastSummarySearchQuery?: string;
  summarySearchNavigableResultCount?: number;
  onGoToPreviousSummarySearchResult?: () => void;
  onGoToNextSummarySearchResult?: () => void;
  medicationSearchQuery?: string;
  onMedicationSearchQueryChange?: (value: string) => void;
  onMedicationSearchClear?: () => void;
  medicationSearchResultCount?: number;
  lastMedicationSearchQuery?: string;
  medicationSearchNavigableResultCount?: number;
  onGoToPreviousMedicationSearchResult?: () => void;
  onGoToNextMedicationSearchResult?: () => void;
  codeSearchQuery?: string;
  onCodeSearchQueryChange?: (value: string) => void;
  onCodeSearchClear?: () => void;
  codeSearchResultCount?: number;
  lastCodeSearchQuery?: string;
  codeSearchNavigableResultCount?: number;
  onGoToPreviousCodeSearchResult?: () => void;
  onGoToNextCodeSearchResult?: () => void;
  disabled: boolean;
};

/*
 * renders the toolbar-level toc filter button and dropdown controls
 * for document type and toc search filtering.
 */
const MetaDocTocFilter = ({
  availableDocumentTypes,
  selectedDocumentTypes,
  onDocumentTypesChange,
  searchQuery,
  onSearchQueryChange,
  pageRangeStart = null,
  pageRangeEnd = null,
  onPageRangeChange,
  dateRangeStart = null,
  dateRangeEnd = null,
  onDateRangeChange,
  currentPage,
  totalPages,
  hideFilteredPages = false,
  onHideFilteredPagesChange,
  summarySearchQuery = "",
  onSummarySearchQueryChange,
  onSummarySearchSubmit,
  onSummarySearchClear,
  isSummarySearching = false,
  summarySearchResultCount = 0,
  lastSummarySearchQuery = "",
  summarySearchNavigableResultCount = 0,
  onGoToPreviousSummarySearchResult,
  onGoToNextSummarySearchResult,
  medicationSearchQuery = "",
  onMedicationSearchQueryChange,
  onMedicationSearchClear,
  medicationSearchResultCount = 0,
  lastMedicationSearchQuery = "",
  medicationSearchNavigableResultCount = 0,
  onGoToPreviousMedicationSearchResult,
  onGoToNextMedicationSearchResult,
  codeSearchQuery = "",
  onCodeSearchQueryChange,
  onCodeSearchClear,
  codeSearchResultCount = 0,
  lastCodeSearchQuery = "",
  codeSearchNavigableResultCount = 0,
  onGoToPreviousCodeSearchResult,
  onGoToNextCodeSearchResult,
  disabled,
}: MetaDocTocFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSection, setExpandedSection] =
    useState<TocFilterSection | null>(null);
  const [pageRangeStartDraft, setPageRangeStartDraft] = useState(
    pageRangeStart?.toString() ?? "",
  );
  const [pageRangeEndDraft, setPageRangeEndDraft] = useState(
    pageRangeEnd?.toString() ?? "",
  );
  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const showAllCheckboxRef = useRef<HTMLInputElement | null>(null);
  const selectedTypeSet = useMemo(
    () => new Set(selectedDocumentTypes),
    [selectedDocumentTypes],
  );
  const isAllDocumentTypesSelected =
    availableDocumentTypes.length > 0 &&
    selectedDocumentTypes.length === availableDocumentTypes.length;
  const isSomeDocumentTypesSelected =
    selectedDocumentTypes.length > 0 && !isAllDocumentTypesSelected;
  const hasActiveDocumentTypeFilter =
    selectedDocumentTypes.length !== availableDocumentTypes.length;
  const { anchorRef, position } = useAnchoredFixedMenuPosition({
    isOpen,
    menuWidth: 340,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (filterContainerRef.current?.contains(target)) {
        return;
      }

      const clickedElement = event.target as HTMLElement;
      if (
        clickedElement.closest(".react-datepicker") ||
        clickedElement.closest(".react-datepicker-popper") ||
        clickedElement.closest(".react-datepicker__portal")
      ) {
        return;
      }

      setIsOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!showAllCheckboxRef.current) {
      return;
    }

    showAllCheckboxRef.current.indeterminate = isSomeDocumentTypesSelected;
  }, [isSomeDocumentTypesSelected]);

  useEffect(() => {
    setPageRangeStartDraft(pageRangeStart?.toString() ?? "");
  }, [pageRangeStart]);

  useEffect(() => {
    setPageRangeEndDraft(pageRangeEnd?.toString() ?? "");
  }, [pageRangeEnd]);

  if (availableDocumentTypes.length === 0) {
    return null;
  }

  const isDocumentTypesExpanded = expandedSection === "documentTypes";
  const isPageRangeExpanded = expandedSection === "pageRange";
  const isDateRangeExpanded = expandedSection === "dateRange";
  const isSummarySearchExpanded = expandedSection === "summarySearch";
  const isMedicationSearchExpanded = expandedSection === "medicationSearch";
  const isCodeSearchExpanded = expandedSection === "codeSearch";
  const isTocSearchExpanded = expandedSection === "tocSearch";
  const hasActiveSearchFilters =
    hasActiveDocumentTypeFilter ||
    pageRangeStart !== null ||
    pageRangeEnd !== null ||
    dateRangeStart !== null ||
    dateRangeEnd !== null ||
    searchQuery.trim().length > 0 ||
    summarySearchQuery.trim().length > 0 ||
    medicationSearchQuery.trim().length > 0 ||
    codeSearchQuery.trim().length > 0;
  const hasNavigableSummarySearchResults =
    summarySearchQuery.trim().length > 0 &&
    summarySearchQuery.trim() === lastSummarySearchQuery &&
    summarySearchNavigableResultCount > 1;
  const hasNavigableMedicationSearchResults =
    medicationSearchQuery.trim().length > 0 &&
    medicationSearchQuery.trim() === lastMedicationSearchQuery &&
    medicationSearchNavigableResultCount > 1;
  const hasNavigableCodeSearchResults =
    codeSearchQuery.trim().length > 0 &&
    codeSearchQuery.trim() === lastCodeSearchQuery &&
    codeSearchNavigableResultCount > 1;
  const isDocumentTypesActive = hasActiveDocumentTypeFilter;
  const isPageRangeActive = pageRangeStart !== null || pageRangeEnd !== null;
  const isDateRangeActive = dateRangeStart !== null || dateRangeEnd !== null;
  const isMedicationSearchActive = medicationSearchQuery.trim().length > 0;
  const isCodeSearchActive = codeSearchQuery.trim().length > 0;
  const isSummarySearchActive = summarySearchQuery.trim().length > 0;
  const isTocSearchActive = searchQuery.trim().length > 0;

  const handleSectionClick = (section: TocFilterSection) => {
    setExpandedSection((currentSection) =>
      currentSection === section ? null : section,
    );
  };

  const parsePageNumber = (value: string) => {
    if (value.trim().length === 0) {
      return null;
    }

    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed)) {
      return null;
    }

    return Math.min(Math.max(parsed, 1), totalPages);
  };

  const updatePageRange = (nextValue: TocPageRangeValue) => {
    onPageRangeChange?.(nextValue);
  };

  const handlePageRangeStartChange = (value: string) => {
    const sanitizedValue = value.replace(/\D/g, "");
    const nextStartPage = parsePageNumber(sanitizedValue);
    const nextEndPage =
      pageRangeEnd !== null &&
      nextStartPage !== null &&
      nextStartPage > pageRangeEnd
        ? nextStartPage
        : pageRangeEnd;

    setPageRangeStartDraft(sanitizedValue);
    setPageRangeEndDraft(nextEndPage?.toString() ?? "");
    updatePageRange({
      startPage: nextStartPage,
      endPage: nextEndPage,
    });
  };

  const handlePageRangeEndChange = (value: string) => {
    const sanitizedValue = value.replace(/\D/g, "");
    const nextEndPage = parsePageNumber(sanitizedValue);
    const nextStartPage =
      pageRangeStart !== null &&
      nextEndPage !== null &&
      nextEndPage < pageRangeStart
        ? nextEndPage
        : pageRangeStart;

    setPageRangeStartDraft(nextStartPage?.toString() ?? "");
    setPageRangeEndDraft(sanitizedValue);
    updatePageRange({
      startPage: nextStartPage,
      endPage: nextEndPage,
    });
  };

  const handlePageRangePreset = (nextValue: TocPageRangeValue) => {
    setPageRangeStartDraft(nextValue.startPage?.toString() ?? "");
    setPageRangeEndDraft(nextValue.endPage?.toString() ?? "");
    updatePageRange(nextValue);
  };

  const handleDateRangeChange = (nextValue: TocDateRangeValue) => {
    onDateRangeChange?.(nextValue);
  };

  const quickRangePresets = [
    {
      label: "All",
      value: { startPage: 1, endPage: totalPages },
    },
    {
      label: "Current page",
      value: { startPage: currentPage, endPage: currentPage },
    },
    {
      label: "First 10",
      value: { startPage: 1, endPage: Math.min(totalPages, 10) },
    },
    {
      label: "Last 10",
      value: {
        startPage: Math.max(1, totalPages - 9),
        endPage: totalPages,
      },
    },
  ].filter((preset, index, allPresets) => {
    if (totalPages === 0) {
      return false;
    }

    if (preset.label === "All") {
      return true;
    }

    return (
      allPresets.findIndex(
        (candidate) =>
          candidate.label !== "All" &&
          candidate.value.startPage === preset.value.startPage &&
          candidate.value.endPage === preset.value.endPage,
      ) === index
    );
  });

  const selectedStartDate = STRING_TO_DATE(dateRangeStart);
  const selectedEndDate = STRING_TO_DATE(dateRangeEnd);
  const quickDatePresets = QUICK_DATE_RANGE_OPTIONS.map((option) => {
    const { start, end } = getQuickDateRange(option.value);

    return {
      label: option.label,
      value: {
        startDate: DATE_TO_STRING(start),
        endDate: DATE_TO_STRING(end),
      },
    };
  });

  return (
    <div
      className="metadoc-toolbar-toc-filter"
      ref={filterContainerRef}
      style={{ position: "relative" }}
    >
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setIsOpen((isCurrentlyOpen) => !isCurrentlyOpen)}
        className={`metadoc-toolbar__nav-button metadoc-toolbar__nav-button--edge metadoc-toolbar__search-toggle metadoc-toolbar-toc-filter__button ${
          isOpen || hasActiveSearchFilters
            ? "metadoc-toolbar-toc-filter__button--active"
            : ""
        }`}
        title="Filter table of contents by document type"
        disabled={disabled}
        aria-expanded={isOpen}
        aria-controls="metadoc-toolbar-toc-filter-dropdown"
      >
        <span className="material-icons">filter_list</span>
        Filter
      </button>
      {isOpen && (
        <div
          id="metadoc-toolbar-toc-filter-dropdown"
          className="metadoc-toolbar-toc-filter__dropdown"
          style={{
            position: "fixed",
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            zIndex: 120,
          }}
        >
          <div className="metadoc-toolbar-toc-filter__section metadoc-toolbar-toc-filter__section--document-types">
            <button
              type="button"
              className={`metadoc-toolbar-toc-filter__section-header ${
                isDocumentTypesExpanded
                  ? "metadoc-toolbar-toc-filter__section-header--expanded"
                  : ""
              } ${
                isDocumentTypesActive
                  ? "metadoc-toolbar-toc-filter__section-header--active"
                  : ""
              }`}
              onClick={() => handleSectionClick("documentTypes")}
              aria-expanded={isDocumentTypesExpanded}
            >
              <span className="material-icons">category</span>
              <span className="metadoc-toolbar-toc-filter__section-title">
                Document Types
              </span>
              <span className="material-icons metadoc-toolbar-toc-filter__expand-icon">
                {isDocumentTypesExpanded ? "expand_more" : "chevron_right"}
              </span>
            </button>
            {isDocumentTypesExpanded && (
              <div className="metadoc-toolbar-toc-filter__section-content metadoc-toolbar-toc-filter__section-content--document-types">
                <label className="metadoc-toolbar-toc-filter__option metadoc-toolbar-toc-filter__option--all">
                  <input
                    ref={showAllCheckboxRef}
                    type="checkbox"
                    className="metadoc-toolbar-toc-filter__option-checkbox"
                    checked={isAllDocumentTypesSelected}
                    onChange={() => {
                      if (!onDocumentTypesChange) {
                        return;
                      }

                      if (isAllDocumentTypesSelected) {
                        onDocumentTypesChange(() => []);
                        return;
                      }

                      onDocumentTypesChange(() => availableDocumentTypes);
                    }}
                  />
                  <span className="metadoc-toolbar-toc-filter__option-label">
                    Show All ({availableDocumentTypes.length} types)
                  </span>
                </label>
                {availableDocumentTypes.map((documentType) => (
                  <label
                    key={documentType}
                    className={`metadoc-toolbar-toc-filter__option ${
                      selectedTypeSet.has(documentType)
                        ? "metadoc-toolbar-toc-filter__option--selected"
                        : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="metadoc-toolbar-toc-filter__option-checkbox"
                      checked={selectedTypeSet.has(documentType)}
                      onChange={() => {
                        if (!onDocumentTypesChange) {
                          return;
                        }

                        onDocumentTypesChange((currentTypes) => {
                          if (currentTypes.includes(documentType)) {
                            return currentTypes.filter(
                              (type) => type !== documentType,
                            );
                          }

                          return [...currentTypes, documentType];
                        });
                      }}
                    />
                    <span className="metadoc-toolbar-toc-filter__option-label">
                      {getDocumentTypeLabel(documentType)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="metadoc-toolbar-toc-filter__section">
            <button
              type="button"
              className={`metadoc-toolbar-toc-filter__section-header ${
                isDateRangeExpanded
                  ? "metadoc-toolbar-toc-filter__section-header--expanded"
                  : ""
              } ${
                isDateRangeActive
                  ? "metadoc-toolbar-toc-filter__section-header--active"
                  : ""
              }`}
              onClick={() => handleSectionClick("dateRange")}
              aria-expanded={isDateRangeExpanded}
            >
              <span className="material-icons">date_range</span>
              <span className="metadoc-toolbar-toc-filter__section-title">
                Date Range
              </span>
              <span className="material-icons metadoc-toolbar-toc-filter__expand-icon">
                {isDateRangeExpanded ? "expand_more" : "chevron_right"}
              </span>
            </button>
            {isDateRangeExpanded && (
              <div className="metadoc-toolbar-toc-filter__section-content">
                <div className="metadoc-toolbar-toc-filter__date-range">
                  <div className="metadoc-toolbar-toc-filter__page-range-inputs">
                    <div className="metadoc-toolbar-toc-filter__page-range-field">
                      <span className="metadoc-toolbar-toc-filter__page-range-label">
                        From
                      </span>
                      <DatePicker
                        key={`toc-date-start-${dateRangeStart ?? "empty"}`}
                        name="toc-date-range-start"
                        initialDate={selectedStartDate ?? undefined}
                        onDateChange={(date) =>
                          handleDateRangeChange({
                            startDate: DATE_TO_STRING(date),
                            endDate:
                              selectedEndDate && date && selectedEndDate < date
                                ? DATE_TO_STRING(date)
                                : dateRangeEnd,
                          })
                        }
                        allowFutureDates={true}
                        className="metadoc-toolbar-toc-filter__date-picker"
                      />
                    </div>
                    <div className="metadoc-toolbar-toc-filter__page-range-field">
                      <span className="metadoc-toolbar-toc-filter__page-range-label">
                        To
                      </span>
                      <DatePicker
                        key={`toc-date-end-${dateRangeEnd ?? "empty"}`}
                        name="toc-date-range-end"
                        initialDate={selectedEndDate ?? undefined}
                        onDateChange={(date) =>
                          handleDateRangeChange({
                            startDate:
                              selectedStartDate &&
                              date &&
                              selectedStartDate > date
                                ? DATE_TO_STRING(date)
                                : dateRangeStart,
                            endDate: DATE_TO_STRING(date),
                          })
                        }
                        allowFutureDates={true}
                        className="metadoc-toolbar-toc-filter__date-picker"
                      />
                    </div>
                  </div>
                  <div className="metadoc-toolbar-toc-filter__page-range-presets">
                    {quickDatePresets.map((preset) => {
                      const isSelected =
                        dateRangeStart === preset.value.startDate &&
                        dateRangeEnd === preset.value.endDate;

                      return (
                        <button
                          key={preset.label}
                          type="button"
                          className={`metadoc-toolbar-toc-filter__page-range-chip ${
                            isSelected
                              ? "metadoc-toolbar-toc-filter__page-range-chip--selected"
                              : ""
                          }`}
                          onClick={() => handleDateRangeChange(preset.value)}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="metadoc-toolbar-toc-filter__page-range-footer">
                    <span>
                      Match entries with related dates {dateRangeStart ?? "any"}
                      -{dateRangeEnd ?? "any"}
                    </span>
                    {(dateRangeStart !== null || dateRangeEnd !== null) && (
                      <button
                        type="button"
                        className="metadoc-toolbar-toc-filter__page-range-clear"
                        onClick={() =>
                          handleDateRangeChange({
                            startDate: null,
                            endDate: null,
                          })
                        }
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="metadoc-toolbar-toc-filter__section">
            <button
              type="button"
              className={`metadoc-toolbar-toc-filter__section-header ${
                isPageRangeExpanded
                  ? "metadoc-toolbar-toc-filter__section-header--expanded"
                  : ""
              } ${
                isPageRangeActive
                  ? "metadoc-toolbar-toc-filter__section-header--active"
                  : ""
              }`}
              onClick={() => handleSectionClick("pageRange")}
              aria-expanded={isPageRangeExpanded}
            >
              <span className="material-icons">filter_alt</span>
              <span className="metadoc-toolbar-toc-filter__section-title">
                Page Range
              </span>
              <span className="material-icons metadoc-toolbar-toc-filter__expand-icon">
                {isPageRangeExpanded ? "expand_more" : "chevron_right"}
              </span>
            </button>
            {isPageRangeExpanded && (
              <div className="metadoc-toolbar-toc-filter__section-content">
                <div className="metadoc-toolbar-toc-filter__page-range">
                  <div className="metadoc-toolbar-toc-filter__page-range-inputs">
                    <label className="metadoc-toolbar-toc-filter__page-range-field">
                      <span className="metadoc-toolbar-toc-filter__page-range-label">
                        From
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="1"
                        value={pageRangeStartDraft}
                        onChange={(event) =>
                          handlePageRangeStartChange(event.target.value)
                        }
                        className="metadoc-toolbar-toc-filter__page-range-input"
                      />
                    </label>
                    <label className="metadoc-toolbar-toc-filter__page-range-field">
                      <span className="metadoc-toolbar-toc-filter__page-range-label">
                        To
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder={totalPages.toString()}
                        value={pageRangeEndDraft}
                        onChange={(event) =>
                          handlePageRangeEndChange(event.target.value)
                        }
                        className="metadoc-toolbar-toc-filter__page-range-input"
                      />
                    </label>
                  </div>
                  <div className="metadoc-toolbar-toc-filter__page-range-presets">
                    {quickRangePresets.map((preset) => {
                      const isSelected =
                        pageRangeStart === preset.value.startPage &&
                        pageRangeEnd === preset.value.endPage;

                      return (
                        <button
                          key={preset.label}
                          type="button"
                          className={`metadoc-toolbar-toc-filter__page-range-chip ${
                            isSelected
                              ? "metadoc-toolbar-toc-filter__page-range-chip--selected"
                              : ""
                          }`}
                          onClick={() => handlePageRangePreset(preset.value)}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="metadoc-toolbar-toc-filter__page-range-footer">
                    <span>
                      Show entries that overlap pages {pageRangeStart ?? 1}-
                      {pageRangeEnd ?? totalPages}
                    </span>
                    {(pageRangeStart !== null || pageRangeEnd !== null) && (
                      <button
                        type="button"
                        className="metadoc-toolbar-toc-filter__page-range-clear"
                        onClick={() =>
                          handlePageRangePreset({
                            startPage: null,
                            endPage: null,
                          })
                        }
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="metadoc-toolbar-toc-filter__section">
            <button
              type="button"
              className={`metadoc-toolbar-toc-filter__section-header ${
                isMedicationSearchExpanded
                  ? "metadoc-toolbar-toc-filter__section-header--expanded"
                  : ""
              } ${
                isMedicationSearchActive
                  ? "metadoc-toolbar-toc-filter__section-header--active"
                  : ""
              }`}
              onClick={() => handleSectionClick("medicationSearch")}
              aria-expanded={isMedicationSearchExpanded}
            >
              <span className="material-icons">medication</span>
              <span className="metadoc-toolbar-toc-filter__section-title">
                Medications
              </span>
              <span className="material-icons metadoc-toolbar-toc-filter__expand-icon">
                {isMedicationSearchExpanded ? "expand_more" : "chevron_right"}
              </span>
            </button>
            {isMedicationSearchExpanded && (
              <div className="metadoc-toolbar-toc-filter__section-content">
                <div className="metadoc-toolbar-toc-filter__search">
                  <span className="material-icons">search</span>
                  <input
                    type="text"
                    placeholder="Search medications, dosage, frequency..."
                    value={medicationSearchQuery}
                    onChange={(event) =>
                      onMedicationSearchQueryChange?.(event.target.value)
                    }
                    className="metadoc-toolbar-toc-filter__search-input"
                  />
                  {medicationSearchQuery && (
                    <button
                      type="button"
                      className="metadoc-toolbar-toc-filter__search-clear"
                      onClick={() => onMedicationSearchClear?.()}
                      aria-label="Clear medication search"
                    >
                      <span className="material-icons">close</span>
                    </button>
                  )}
                </div>
                {medicationSearchQuery.trim().length > 0 && (
                  <div className="metadoc-toolbar-toc-filter__search-meta">
                    <span>
                      {medicationSearchResultCount} result
                      {medicationSearchResultCount === 1 ? "" : "s"}
                    </span>
                    <div className="metadoc-toolbar-toc-filter__search-navigation">
                      <button
                        type="button"
                        className="metadoc-toolbar-toc-filter__search-nav-button"
                        onClick={() => onGoToPreviousMedicationSearchResult?.()}
                        disabled={!hasNavigableMedicationSearchResults}
                        aria-label="Previous medication search result"
                      >
                        <span className="material-icons">chevron_left</span>
                      </button>
                      <button
                        type="button"
                        className="metadoc-toolbar-toc-filter__search-nav-button"
                        onClick={() => onGoToNextMedicationSearchResult?.()}
                        disabled={!hasNavigableMedicationSearchResults}
                        aria-label="Next medication search result"
                      >
                        <span className="material-icons">chevron_right</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="metadoc-toolbar-toc-filter__section">
            <button
              type="button"
              className={`metadoc-toolbar-toc-filter__section-header ${
                isCodeSearchExpanded
                  ? "metadoc-toolbar-toc-filter__section-header--expanded"
                  : ""
              } ${
                isCodeSearchActive
                  ? "metadoc-toolbar-toc-filter__section-header--active"
                  : ""
              }`}
              onClick={() => handleSectionClick("codeSearch")}
              aria-expanded={isCodeSearchExpanded}
            >
              <span className="material-icons">code</span>
              <span className="metadoc-toolbar-toc-filter__section-title">
                Codes
              </span>
              <span className="material-icons metadoc-toolbar-toc-filter__expand-icon">
                {isCodeSearchExpanded ? "expand_more" : "chevron_right"}
              </span>
            </button>
            {isCodeSearchExpanded && (
              <div className="metadoc-toolbar-toc-filter__section-content">
                <div className="metadoc-toolbar-toc-filter__search">
                  <span className="material-icons">search</span>
                  <input
                    type="text"
                    placeholder="Search ICD, HCPCS, CPT, notes..."
                    value={codeSearchQuery}
                    onChange={(event) =>
                      onCodeSearchQueryChange?.(event.target.value)
                    }
                    className="metadoc-toolbar-toc-filter__search-input"
                  />
                  {codeSearchQuery && (
                    <button
                      type="button"
                      className="metadoc-toolbar-toc-filter__search-clear"
                      onClick={() => onCodeSearchClear?.()}
                      aria-label="Clear code search"
                    >
                      <span className="material-icons">close</span>
                    </button>
                  )}
                </div>
                {codeSearchQuery.trim().length > 0 && (
                  <div className="metadoc-toolbar-toc-filter__search-meta">
                    <span>
                      {codeSearchResultCount} result
                      {codeSearchResultCount === 1 ? "" : "s"}
                    </span>
                    <div className="metadoc-toolbar-toc-filter__search-navigation">
                      <button
                        type="button"
                        className="metadoc-toolbar-toc-filter__search-nav-button"
                        onClick={() => onGoToPreviousCodeSearchResult?.()}
                        disabled={!hasNavigableCodeSearchResults}
                        aria-label="Previous code search result"
                      >
                        <span className="material-icons">chevron_left</span>
                      </button>
                      <button
                        type="button"
                        className="metadoc-toolbar-toc-filter__search-nav-button"
                        onClick={() => onGoToNextCodeSearchResult?.()}
                        disabled={!hasNavigableCodeSearchResults}
                        aria-label="Next code search result"
                      >
                        <span className="material-icons">chevron_right</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="metadoc-toolbar-toc-filter__section">
            <button
              type="button"
              className={`metadoc-toolbar-toc-filter__section-header ${
                isSummarySearchExpanded
                  ? "metadoc-toolbar-toc-filter__section-header--expanded"
                  : ""
              } ${
                isSummarySearchActive
                  ? "metadoc-toolbar-toc-filter__section-header--active"
                  : ""
              }`}
              onClick={() => handleSectionClick("summarySearch")}
              aria-expanded={isSummarySearchExpanded}
            >
              <span className="material-icons">summarize</span>
              <span className="metadoc-toolbar-toc-filter__section-title">
                Summary
              </span>
              <span className="material-icons metadoc-toolbar-toc-filter__expand-icon">
                {isSummarySearchExpanded ? "expand_more" : "chevron_right"}
              </span>
            </button>
            {isSummarySearchExpanded && (
              <div className="metadoc-toolbar-toc-filter__section-content">
                <div className="metadoc-toolbar-toc-filter__search">
                  <span className="material-icons">search</span>
                  <input
                    type="text"
                    placeholder="Search executive and per-record summaries..."
                    value={summarySearchQuery}
                    onChange={(event) =>
                      onSummarySearchQueryChange?.(event.target.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        onSummarySearchSubmit?.();
                      }
                    }}
                    className="metadoc-toolbar-toc-filter__search-input"
                  />
                  <button
                    type="button"
                    className="metadoc-toolbar-toc-filter__search-action"
                    onClick={() => onSummarySearchSubmit?.()}
                    disabled={
                      disabled ||
                      isSummarySearching ||
                      summarySearchQuery.trim().length === 0
                    }
                    aria-label="Search summaries"
                  >
                    <span className="material-icons">
                      {isSummarySearching ? "hourglass_top" : "search"}
                    </span>
                  </button>
                  {summarySearchQuery && (
                    <button
                      type="button"
                      className="metadoc-toolbar-toc-filter__search-clear"
                      onClick={() => onSummarySearchClear?.()}
                      aria-label="Clear summary search"
                    >
                      <span className="material-icons">close</span>
                    </button>
                  )}
                </div>
                {summarySearchQuery.trim().length > 0 && (
                  <div className="metadoc-toolbar-toc-filter__search-meta">
                    <span>
                      {isSummarySearching
                        ? "Searching summaries..."
                        : `${summarySearchResultCount} result${summarySearchResultCount === 1 ? "" : "s"}`}
                    </span>
                    <div className="metadoc-toolbar-toc-filter__search-navigation">
                      <button
                        type="button"
                        className="metadoc-toolbar-toc-filter__search-nav-button"
                        onClick={() => onGoToPreviousSummarySearchResult?.()}
                        disabled={!hasNavigableSummarySearchResults}
                        aria-label="Previous summary search result"
                      >
                        <span className="material-icons">chevron_left</span>
                      </button>
                      <button
                        type="button"
                        className="metadoc-toolbar-toc-filter__search-nav-button"
                        onClick={() => onGoToNextSummarySearchResult?.()}
                        disabled={!hasNavigableSummarySearchResults}
                        aria-label="Next summary search result"
                      >
                        <span className="material-icons">chevron_right</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="metadoc-toolbar-toc-filter__section">
            <button
              type="button"
              className={`metadoc-toolbar-toc-filter__section-header ${
                isTocSearchExpanded
                  ? "metadoc-toolbar-toc-filter__section-header--expanded"
                  : ""
              } ${
                isTocSearchActive
                  ? "metadoc-toolbar-toc-filter__section-header--active"
                  : ""
              }`}
              onClick={() => handleSectionClick("tocSearch")}
              aria-expanded={isTocSearchExpanded}
            >
              <span className="material-icons">person_search</span>
              <span className="metadoc-toolbar-toc-filter__section-title">
                Title/Provider/Facility
              </span>
              <span className="material-icons metadoc-toolbar-toc-filter__expand-icon">
                {isTocSearchExpanded ? "expand_more" : "chevron_right"}
              </span>
            </button>
            {isTocSearchExpanded && (
              <div className="metadoc-toolbar-toc-filter__section-content">
                <div className="metadoc-toolbar-toc-filter__search">
                  <span className="material-icons">search</span>
                  <input
                    type="text"
                    placeholder="Search title, provider, facility..."
                    value={searchQuery}
                    onChange={(event) =>
                      onSearchQueryChange?.(event.target.value)
                    }
                    className="metadoc-toolbar-toc-filter__search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="metadoc-toolbar-toc-filter__search-clear"
                      onClick={() => onSearchQueryChange?.("")}
                      aria-label="Clear table of contents search"
                    >
                      <span className="material-icons">close</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="metadoc-toolbar-toc-filter__footer-toggle">
            <span className="metadoc-toolbar-toc-filter__footer-toggle-label">
              Hide filtered pages
            </span>
            <Toggle
              checked={hideFilteredPages}
              onChange={(checked) => onHideFilteredPagesChange?.(checked)}
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaDocTocFilter;
